import React, {
  useCallback,
  useEffect,
  useState,
} from 'react'
import {
  createRfidTag,
  deleteRfidTag,
  updateRfidTag,
} from '../../modules/Rfid'
import { getErrorString } from '../../utils/errorUtils'

type Props = {
  group: IGroup
  tags: IRfidTag[]
  onTagsChanged: () => Promise<void>
}

const RfidTagManagement = ({
  group,
  tags,
  onTagsChanged,
}: Props) => {
  const [editingTag, setEditingTag] = useState<IRfidTag | null>(null)
  const [name, setName] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [payload, setPayload] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<IRfidTag | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const resetForm = useCallback(() => {
    setEditingTag(null)
    setName('')
    setIdentifier('')
    setPayload('')
    setError(null)
  }, [])

  useEffect(() => {
    resetForm()
    setDeleteTarget(null)
    setMessage(null)
  }, [group.id, resetForm])

  const handleEdit = (tag: IRfidTag) => {
    setEditingTag(tag)
    setName(tag.name)
    setIdentifier(tag.identifier)
    setPayload(tag.payload ?? '')
    setError(null)
    setMessage(null)
  }

  const handleSubmit = async () => {
    const trimmedName = name.trim()
    const trimmedIdentifier = identifier.trim()
    if (!trimmedName || !trimmedIdentifier) {
      setError('名前とRFID識別子を入力してください。')
      return
    }

    setIsSaving(true)
    setError(null)
    setMessage(null)
    try {
      const attributes = {
        name: trimmedName,
        identifier: trimmedIdentifier,
        payload: payload.trim() || null,
      }
      if (editingTag) {
        await updateRfidTag(group.id, editingTag.id, attributes)
        setMessage('RFIDタグを更新しました。')
      } else {
        await createRfidTag(group.id, attributes)
        setMessage('RFIDタグを登録しました。')
      }
      resetForm()
      await onTagsChanged()
    } catch (submitError) {
      setError(getErrorString(submitError))
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return

    setIsSaving(true)
    setError(null)
    setMessage(null)
    try {
      await deleteRfidTag(group.id, deleteTarget.id)
      if (editingTag?.id === deleteTarget.id) resetForm()
      setDeleteTarget(null)
      setMessage('RFIDタグを削除しました。')
      await onTagsChanged()
    } catch (deleteError) {
      setError(getErrorString(deleteError))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <section className="rfid-panel" data-testid="rfid-tag-management">
      <h3>RFIDタグ管理</h3>
      <p className="rfid-panel__description">
        このグループで使用するRFIDタグを登録します。識別子とペイロードには英数字以外も入力できます。
      </p>

      <div className="rfid-tag-form">
        <div className="rfid-field">
          <label htmlFor="rfid-tag-name">名前*</label>
          <input
            id="rfid-tag-name"
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="rfid-field">
          <label htmlFor="rfid-tag-identifier">RFID識別子*</label>
          <input
            id="rfid-tag-identifier"
            type="text"
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
          />
        </div>
        <div className="rfid-field">
          <label htmlFor="rfid-tag-payload">ペイロード</label>
          <input
            id="rfid-tag-payload"
            type="text"
            value={payload}
            onChange={(event) => setPayload(event.target.value)}
          />
        </div>
        <div className="rfid-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={isSaving}
            onClick={handleSubmit}
          >
            {editingTag ? '更新する' : '登録する'}
          </button>
          {editingTag && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={isSaving}
              onClick={resetForm}
            >
              編集をやめる
            </button>
          )}
        </div>
      </div>

      {message && <div className="rfid-message rfid-message--success">{message}</div>}
      {error && <div className="rfid-message rfid-message--error">{error}</div>}

      <div className="rfid-table-wrapper">
        <table className="table">
          <thead>
            <tr>
              <th>名前</th>
              <th>RFID識別子</th>
              <th>ペイロード</th>
              <th aria-label="操作" />
            </tr>
          </thead>
          <tbody>
            {tags.length === 0 ? (
              <tr>
                <td colSpan={4} className="rfid-table-empty">RFIDタグはまだ登録されていません。</td>
              </tr>
            ) : tags.map((tag) => (
              <tr key={tag.id}>
                <td>{tag.name}</td>
                <td className="rfid-code">{tag.identifier}</td>
                <td className="rfid-code">{tag.payload || '—'}</td>
                <td className="rfid-table-actions">
                  {deleteTarget?.id === tag.id ? (
                    <div className="rfid-delete-confirmation">
                      <span>削除しますか？</span>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        disabled={isSaving}
                        onClick={handleDelete}
                      >
                        削除
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={isSaving}
                        onClick={() => setDeleteTarget(null)}
                      >
                        戻る
                      </button>
                    </div>
                  ) : (
                    <div className="rfid-actions">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        disabled={isSaving}
                        onClick={() => handleEdit(tag)}
                      >
                        編集
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        disabled={isSaving}
                        onClick={() => setDeleteTarget(tag)}
                      >
                        削除
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export default RfidTagManagement
